import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '@/app/store';
import { useSocket } from '@/contexts/SocketContext';
import { useAuth } from '@/core/auth/AuthContext';
import { useTrackerUnreadContext } from '../contexts/TrackerUnreadContext';
import { playNotificationSound } from '@/utils/notificationSound';
import { useSettings } from '@/features/shared/settings/hooks/useSettings';

interface UseTrackerNotificationsOptions {
  enabled?: boolean;
}

interface TrackerTaskCreatedEvent {
  channelId: string;
  affaireId: number;
  task: any;
  createdBy: { id: string; name: string };
  timestamp: string;
}

interface TrackerTaskUpdatedEvent {
  channelId: string;
  affaireId: number;
  taskId: string;
  task: any;
  updatedBy: { id: string; name: string };
  timestamp: string;
}

interface TrackerCommentAddedEvent {
  channelId: string;
  affaireId: number;
  taskId: string;
  comment: any;
  addedBy: { id: string; name: string };
  timestamp: string;
}

interface TrackerAffaireTaggedEvent {
  affaireId: number;
  todo: {
    priority: 'none' | 'low' | 'medium' | 'high' | 'urgent';
    comment?: string;
    tagged_user: { id: string; first_name: string; last_name: string; email: string };
    created_by: { id: string; first_name: string; last_name: string; email: string };
  };
  timestamp: string;
}

interface TrackerCommunicationAssignedEvent {
  affaireId: number;
  communicationType: 'tracker_com' | 'com_internes';
  communicationId: string;
  content?: string | null;
  urgency: 'low' | 'medium' | 'high' | 'urgent';
  comment?: string | null;
  assignedBy: { id: string; name: string };
  timestamp: string;
}

interface TrackerCommunicationCompletedEvent {
  affaireId: number;
  communicationType: 'tracker_com' | 'com_internes';
  communicationId: string;
  content?: string | null;
  doneComment?: string | null;
  completedBy: { id: string; name: string };
  timestamp: string;
}

interface TrackerComCreatedEvent {
  affaireId: number;
  trackerCom: any;
  content?: string | null;
  author: { id: string; name: string };
  timestamp: string;
}

interface TrackerSmsReceivedEvent {
  affaireId: number;
  smsId: string;
  phoneNumber: string;
  message?: string;
  senderName?: string;
  timestamp: string;
}

/**
 * Hook to handle tracker notifications
 * Listens to WebSocket events and shows in-app notifications with sound alerts
 */
export const useTrackerNotifications = ({
  enabled = true
}: UseTrackerNotificationsOptions = {}) => {
  const addNotification = useAppStore(state => state.addNotification);
  const { refetchUnreadCount } = useTrackerUnreadContext();
  const notifiedEvents = useRef<Set<string>>(new Set());
  const MAX_NOTIFIED_EVENTS = 1000; // Prevent unlimited growth
  const { socket } = useSocket();
  const { settings } = useSettings();
  const { user } = useAuth();

  // Get the correct tracker URL based on user role
  const getTrackerLink = useCallback((affaireId: number) => {
    const baseUrl = user?.role === 'bureau' ? '/bureau' : '/admin';
    return `${baseUrl}/tracker/${affaireId}`;
  }, [user?.role]);

  // Show notification for new task
  const showTaskCreatedNotification = useCallback((event: TrackerTaskCreatedEvent) => {
    const title = `Nouvelle tâche - Affaire #${event.affaireId}`;
    const message = event.task.message.length > 100
      ? event.task.message.substring(0, 100) + '...'
      : event.task.message;
    const createdBy = event.createdBy.name || 'Inconnu';

    // In-app notification
    addNotification({
      type: 'info',
      title,
      message: `${createdBy}: ${message}`,
      link: getTrackerLink(event.affaireId)
    });

    // Play notification sound if enabled
    if (settings?.soundNotificationsEnabled !== false) {
      playNotificationSound();
    }

    // Refetch unread count
    refetchUnreadCount();
  }, [addNotification, refetchUnreadCount, settings, getTrackerLink]);

  // Show notification for task update
  const showTaskUpdatedNotification = useCallback((event: TrackerTaskUpdatedEvent) => {
    const title = `Tâche mise à jour - Affaire #${event.affaireId}`;
    const message = event.task.message.length > 100
      ? event.task.message.substring(0, 100) + '...'
      : event.task.message;
    const updatedBy = event.updatedBy.name || 'Inconnu';

    // In-app notification
    addNotification({
      type: 'info',
      title,
      message: `${updatedBy}: ${message}`,
      link: getTrackerLink(event.affaireId)
    });

    // Play notification sound if enabled
    if (settings?.soundNotificationsEnabled !== false) {
      playNotificationSound();
    }

    // Refetch unread count
    refetchUnreadCount();
  }, [addNotification, refetchUnreadCount, settings, getTrackerLink]);

  // Show notification for new comment
  const showCommentNotification = useCallback((event: TrackerCommentAddedEvent) => {
    const title = `Nouveau commentaire - Affaire #${event.affaireId}`;
    const message = event.comment.message.length > 100
      ? event.comment.message.substring(0, 100) + '...'
      : event.comment.message;
    const addedBy = event.addedBy.name || 'Inconnu';

    // In-app notification
    addNotification({
      type: 'info',
      title,
      message: `${addedBy}: ${message}`,
      link: getTrackerLink(event.affaireId)
    });

    // Play notification sound if enabled
    if (settings?.soundNotificationsEnabled !== false) {
      playNotificationSound();
    }

    // Refetch unread count
    refetchUnreadCount();
  }, [addNotification, refetchUnreadCount, settings, getTrackerLink]);

  // Show notification for being tagged in affaire
  const showTaggedNotification = useCallback((event: TrackerAffaireTaggedEvent) => {
    const priorityEmoji = {
      urgent: '🔴',
      high: '🟠',
      medium: '🔵',
      low: '⚪',
      none: '➖'
    }[event.todo.priority];

    const createdBy = `${event.todo.created_by.first_name} ${event.todo.created_by.last_name}`;
    const title = `${priorityEmoji} Nouvelle attribution - Affaire #${event.affaireId}`;
    const message = event.todo.comment
      ? `${createdBy}: ${event.todo.comment}`
      : `${createdBy} vous a attribué cette affaire`;

    // In-app notification
    addNotification({
      type: event.todo.priority === 'urgent' || event.todo.priority === 'high' ? 'warning' : 'info',
      title,
      message,
      link: getTrackerLink(event.affaireId)
    });

    // Play notification sound if enabled
    if (settings?.soundNotificationsEnabled !== false) {
      playNotificationSound();
    }

    // Refetch unread count
    refetchUnreadCount();
  }, [addNotification, refetchUnreadCount, settings, getTrackerLink]);

  // Show notification for communication assignment
  const showCommunicationAssignedNotification = useCallback((event: TrackerCommunicationAssignedEvent) => {
    const priorityEmoji = {
      urgent: '🔴',
      high: '🟠',
      medium: '🔵',
      low: '⚪'
    }[event.urgency] || '🔵';

    const commTypeName = event.communicationType === 'tracker_com' ? 'message' : 'communication interne';
    const title = `${priorityEmoji} Nouveau ${commTypeName} - Affaire #${event.affaireId}`;
    const message = event.content
      ? `${event.assignedBy.name}: ${event.content}`
      : `${event.assignedBy.name} vous a assigné un ${commTypeName}`;

    // In-app notification
    addNotification({
      type: event.urgency === 'urgent' || event.urgency === 'high' ? 'warning' : 'info',
      title,
      message,
      link: getTrackerLink(event.affaireId)
    });

    // Play notification sound if enabled
    if (settings?.soundNotificationsEnabled !== false) {
      playNotificationSound();
    }

    // Refetch unread count
    refetchUnreadCount();
  }, [addNotification, refetchUnreadCount, settings, getTrackerLink]);

  // Show notification when communication is completed
  const showCommunicationCompletedNotification = useCallback((event: TrackerCommunicationCompletedEvent) => {
    const commTypeName = event.communicationType === 'tracker_com' ? 'message' : 'communication interne';
    const title = `✅ ${commTypeName.charAt(0).toUpperCase() + commTypeName.slice(1)} terminé - Affaire #${event.affaireId}`;
    const message = event.doneComment
      ? `${event.completedBy.name}: ${event.doneComment}`
      : `${event.completedBy.name} a terminé le ${commTypeName}`;

    // In-app notification
    addNotification({
      type: 'success',
      title,
      message,
      link: getTrackerLink(event.affaireId)
    });

    // Play notification sound if enabled
    if (settings?.soundNotificationsEnabled !== false) {
      playNotificationSound();
    }

    // Refetch unread count
    refetchUnreadCount();
  }, [addNotification, refetchUnreadCount, settings, getTrackerLink]);

  // Show notification for new tracker_com (for responsables)
  const showTrackerComCreatedNotification = useCallback((event: TrackerComCreatedEvent) => {
    const title = `💬 Nouveau message - Affaire #${event.affaireId}`;
    const content = event.content
      ? (event.content.length > 100 ? event.content.substring(0, 100) + '...' : event.content)
      : 'Nouveau message';
    const message = `${event.author.name}: ${content}`;

    // In-app notification
    addNotification({
      type: 'info',
      title,
      message,
      link: getTrackerLink(event.affaireId)
    });

    // Play notification sound if enabled
    if (settings?.soundNotificationsEnabled !== false) {
      playNotificationSound();
    }

    // Refetch unread count
    refetchUnreadCount();
  }, [addNotification, refetchUnreadCount, settings, getTrackerLink]);

  // Show notification for received SMS (for responsables)
  const showSmsReceivedNotification = useCallback((event: TrackerSmsReceivedEvent) => {
    const sender = event.senderName || event.phoneNumber;
    const title = `📱 SMS reçu - Affaire #${event.affaireId}`;
    const content = event.message
      ? (event.message.length > 80 ? event.message.substring(0, 80) + '...' : event.message)
      : 'Nouveau SMS';
    const message = `${sender}: ${content}`;

    // In-app notification
    addNotification({
      type: 'info',
      title,
      message,
      link: getTrackerLink(event.affaireId)
    });

    // Play notification sound if enabled
    if (settings?.soundNotificationsEnabled !== false) {
      playNotificationSound();
    }

    // Refetch unread count
    refetchUnreadCount();
  }, [addNotification, refetchUnreadCount, settings, getTrackerLink]);

  // Set up WebSocket listeners
  useEffect(() => {
    if (!enabled || !socket) {
      return;
    }

    const handleTaskCreated = (event: TrackerTaskCreatedEvent) => {
      console.log('[TrackerNotifications] Received tracker:task:created event:', event);

      // Use event-specific key to prevent duplicate notifications
      const eventKey = `tracker:task:created-${event.task.id}-${event.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        console.log('[TrackerNotifications] Already notified for event:', eventKey);
        return;
      }

      showTaskCreatedNotification(event);
      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    const handleTaskUpdated = (event: TrackerTaskUpdatedEvent) => {
      console.log('[TrackerNotifications] Received tracker:task:updated event:', event);

      // Use event-specific key to prevent duplicate notifications
      const eventKey = `tracker:task:updated-${event.taskId}-${event.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        console.log('[TrackerNotifications] Already notified for event:', eventKey);
        return;
      }

      showTaskUpdatedNotification(event);
      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    const handleCommentAdded = (event: TrackerCommentAddedEvent) => {
      console.log('[TrackerNotifications] Received tracker:task:comment:added event:', event);

      // Use event-specific key to prevent duplicate notifications
      const eventKey = `tracker:comment:added-${event.taskId}-${event.comment.id}-${event.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        console.log('[TrackerNotifications] Already notified for event:', eventKey);
        return;
      }

      showCommentNotification(event);
      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    const handleAffaireTagged = (event: TrackerAffaireTaggedEvent) => {
      console.log('[TrackerNotifications] Received tracker:affaire:tagged event:', event);

      // Use event-specific key to prevent duplicate notifications
      const eventKey = `tracker:affaire:tagged-${event.affaireId}-${event.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        console.log('[TrackerNotifications] Already notified for event:', eventKey);
        return;
      }

      showTaggedNotification(event);
      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    const handleCommunicationAssigned = (event: TrackerCommunicationAssignedEvent) => {
      console.log('[TrackerNotifications] Received tracker:communication:assigned event:', event);

      // Use event-specific key to prevent duplicate notifications
      const eventKey = `tracker:communication:assigned-${event.communicationId}-${event.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        console.log('[TrackerNotifications] Already notified for event:', eventKey);
        return;
      }

      showCommunicationAssignedNotification(event);
      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    const handleCommunicationCompleted = (event: TrackerCommunicationCompletedEvent) => {
      console.log('[TrackerNotifications] Received tracker:communication:completed event:', event);

      // Use event-specific key to prevent duplicate notifications
      const eventKey = `tracker:communication:completed-${event.communicationId}-${event.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        console.log('[TrackerNotifications] Already notified for event:', eventKey);
        return;
      }

      showCommunicationCompletedNotification(event);
      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    const handleTrackerComCreated = (event: TrackerComCreatedEvent) => {
      console.log('[TrackerNotifications] Received tracker:com:created event:', event);

      // Use event-specific key to prevent duplicate notifications
      const eventKey = `tracker:com:created-${event.trackerCom.id}-${event.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        console.log('[TrackerNotifications] Already notified for event:', eventKey);
        return;
      }

      showTrackerComCreatedNotification(event);
      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    const handleSmsReceived = (event: TrackerSmsReceivedEvent) => {
      console.log('[TrackerNotifications] Received tracker:sms:received event:', event);

      // Use event-specific key to prevent duplicate notifications
      const eventKey = `tracker:sms:received-${event.smsId}-${event.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        console.log('[TrackerNotifications] Already notified for event:', eventKey);
        return;
      }

      showSmsReceivedNotification(event);
      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    // Listen for tracker-specific events
    socket.on('tracker:task:created', handleTaskCreated);
    socket.on('tracker:task:updated', handleTaskUpdated);
    socket.on('tracker:task:comment:added', handleCommentAdded);
    socket.on('tracker:affaire:tagged', handleAffaireTagged);
    socket.on('tracker:communication:assigned', handleCommunicationAssigned);
    socket.on('tracker:communication:completed', handleCommunicationCompleted);
    socket.on('tracker:com:created', handleTrackerComCreated);
    socket.on('tracker:sms:received', handleSmsReceived);

    console.log('[TrackerNotifications] Listening for tracker notification events');

    // Cleanup
    return () => {
      socket.off('tracker:task:created', handleTaskCreated);
      socket.off('tracker:task:updated', handleTaskUpdated);
      socket.off('tracker:task:comment:added', handleCommentAdded);
      socket.off('tracker:affaire:tagged', handleAffaireTagged);
      socket.off('tracker:communication:assigned', handleCommunicationAssigned);
      socket.off('tracker:communication:completed', handleCommunicationCompleted);
      socket.off('tracker:com:created', handleTrackerComCreated);
      socket.off('tracker:sms:received', handleSmsReceived);
    };
  }, [enabled, socket, showTaskCreatedNotification, showTaskUpdatedNotification, showCommentNotification, showTaggedNotification, showCommunicationAssignedNotification, showCommunicationCompletedNotification, showTrackerComCreatedNotification, showSmsReceivedNotification]);

  return {
    enabled
  };
};

export default useTrackerNotifications;
